import {BrowserRouter,Routes,Route,useLocation} from "react-router-dom";
import {useEffect,useState} from "react";

// =====================================================
// COMMON COMPONENTS
// =====================================================

import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Features from "./components/Features";
import Footer from "./components/Footer";
import FloatingMenu from "./components/FloatingMenu";
import VideoBackground from "./components/VideoBackground";

// =====================================================
// MCQ PAGES
// =====================================================

import McqHome from "./pages/McqHome";
import McqSubject from "./pages/McqSubject";
import McqTeacher from "./pages/McqTeacher";
import McqModule from "./pages/McqModule";

// =====================================================
// AUTH / MAIN
// =====================================================

import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import Payment from "./pages/Payment";

// =====================================================
// MCQ MODES
// =====================================================

import PaidMCQPage from "./pages/PaidMCQPage";
import FreeMCQPage from "./pages/FreeMCQPage";

// =====================================================
// MIDDLE / GRADE / STREAM
// =====================================================

import MiddlePage from "./pages/MiddlePage";
import GradePage from "./pages/GradePage";
import StreamPage from "./pages/StreamPage";

// =====================================================
// PROFILE
// =====================================================

import Profile from "./pages/Profile";
import ProfileView from "./pages/ProfileView";
import ProfileHistory from "./pages/ProfileHistory";

// =====================================================
// ESSAY
// =====================================================

import EssayPage from "./pages/EssayPage";
import Essay from "./pages/Essay";
import EssayHistory from "./pages/EssayHistory";
import EssayPaid from "./pages/EssayPaid";

// =====================================================
// ANIMATION / VIDEO
// =====================================================

import AnimationPage from "./pages/AnimationPage";
import VideoGallery from "./pages/VideoGallery";

// =====================================================
// TEACHER DEMO
// =====================================================

import TeacherDemo from "./pages/TeacherDemo";

// =====================================================
// EDUNET
// =====================================================

import EdunetLanding from "./pages/EdunetLanding";
import EducationTypePage from "./pages/EducationTypePage";

// =====================================================
// NEW SELECTION FLOW
// =====================================================

import GradeSelection from "./pages/GradeSelection";
import SubjectSelection from "./pages/SubjectSelection";
import ModuleSelection from "./pages/ModuleSelection";
import TeacherSelection from "./pages/TeacherSelection";
import TeacherGradeSelection from "./pages/TeacherGradeSelection";

// =====================================================
// APP ROUTES
// =====================================================

function AppRoutes() {
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);

  // ===================================================
  // PAGE CONDITIONS
  // ===================================================

  const isTeacherDemo =
    location.pathname === "/teacher-demo";

  const isEdunetStart =
    location.pathname === "/edunet-start";

  const isEducationType =
    location.pathname === "/education-type";

  const isAuthPage =
    location.pathname === "/auth";

  const isEdunetPage =
    isEdunetStart;

  const isGradeSelection =
    location.pathname === "/grade-selection";

  const isSubjectSelection =
    location.pathname.startsWith("/subject-selection/");

  const isModuleSelection =
    location.pathname.startsWith("/module-selection/");

  const isTeacherSelection =
    location.pathname.startsWith("/teacher-selection/");

  const isTeacherGradeSelection =
    location.pathname.startsWith("/teacher-grades/");

  const isNewMCQPage =
    location.pathname.startsWith("/mcq/free/teacher/");

  // ===================================================
  // BODY BACKGROUND
  // ===================================================

  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.padding = "0";

    if (isTeacherDemo) {
      document.body.style.background = "#141414";
    } else if (isEdunetStart) {
      document.body.style.background = "#080b20";
    } else if (isEducationType) {
      document.body.style.background = "#080b20";
    } else {
      document.body.style.background = "";
    }

    return () => {
      document.body.style.margin = "";
      document.body.style.padding = "";
      document.body.style.background = "";
    };
  }, [isTeacherDemo, isEdunetStart, isEducationType]);

  // ===================================================
  // POPUP CONTROL
  // ===================================================

  useEffect(() => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) return;

    const user = JSON.parse(storedUser);

    if (!user) return;

    if (location.pathname !== "/") return;

    const navEntry =
      performance.getEntriesByType("navigation")[0];

    const navType = navEntry?.type;

    const fromInternal =
      sessionStorage.getItem("from_internal");

    const showGreeting = () => {
      setShowPopup(true);

      setTimeout(() => {
        setShowPopup(false);
      }, 30000);
    };

    if (navType === "reload") {
      showGreeting();
    } else if (!fromInternal) {
      showGreeting();
    }

    sessionStorage.removeItem("from_internal");
  }, [location.pathname]);

  // ===================================================
  // INTERNAL PAGE TRACK
  // ===================================================

  useEffect(() => {
    if (location.pathname !== "/") {
      sessionStorage.setItem("from_internal", "yes");
    }
  }, [location.pathname]);

  // ===================================================
  // PAGE
  // ===================================================

  return (
    <>
      {/* =================================================
          VIDEO BACKGROUND
      ================================================= */}

      {!isTeacherDemo &&
       !isEdunetPage &&
       !isAuthPage &&
       !isGradeSelection &&
       !isSubjectSelection &&
       !isModuleSelection &&
       !isTeacherSelection &&
       !isTeacherGradeSelection &&
       !isNewMCQPage && (
        <VideoBackground />
      )}

      {/* =================================================
          MAIN WRAPPER
      ================================================= */}

      <div
        style={{
          position: "relative",
          zIndex: 10,
          minHeight: "100vh",
          width: "100%",
          margin: 0,
          padding: 0,
          background: isTeacherDemo
            ? "#141414"
            : "transparent"
        }}
      >

        {/* =================================================
            GLOBAL NAVBAR

            Hidden:
            - Teacher Demo
            - Edunet Splash
            - Education Type
            - Auth
            - Selection pages
            - Free MCQ
        ================================================= */}

        {!isTeacherDemo &&
         !isEdunetPage &&
         !isEducationType &&
         !isAuthPage &&
         !isGradeSelection &&
         !isSubjectSelection &&
         !isModuleSelection &&
         !isTeacherSelection &&
         !isTeacherGradeSelection &&
         !isNewMCQPage && (
          <Navbar />
        )}

        {/* =================================================
            GLOBAL FLOATING MENU

            Hidden:
            - Teacher Demo
            - Edunet Splash
            - Education Type
            - Auth
            - Selection pages
            - Free MCQ
        ================================================= */}

        {!isTeacherDemo &&
         !isEdunetPage &&
         !isEducationType &&
         !isAuthPage &&
         !isGradeSelection &&
         !isSubjectSelection &&
         !isModuleSelection &&
         !isTeacherSelection &&
         !isTeacherGradeSelection &&
         !isNewMCQPage && (
          <FloatingMenu />
        )}

        {/* =================================================
            ROUTES
        ================================================= */}

        <Routes>

          {/* =================================================
              HOME
          ================================================= */}

          <Route
            path="/"
            element={
              <>
                <Hero showPopup={showPopup} />
                <Features />
              </>
            }
          />

          {/* =================================================
              FEATURES
          ================================================= */}

          <Route
            path="/mcq"
            element={<McqHome />}
          />

          <Route
            path="/past-papers"
            element={<McqHome />}
          />

          <Route
            path="/videos"
            element={<McqHome />}
          />

          <Route
            path="/model-papers"
            element={<McqHome />}
          />

          {/* =================================================
              MIDDLE
          ================================================= */}

          <Route
            path="/mcq/middle/:id"
            element={<MiddlePage />}
          />

          <Route
            path="/:feature/middle/:id"
            element={<MiddlePage />}
          />

          {/* =================================================
              OLD MCQ FLOW
          ================================================= */}

          <Route
            path="/mcq/grade/:id"
            element={<McqHome />}
          />

          <Route
            path="/mcq/subject/:id"
            element={<McqSubject />}
          />

          <Route
            path="/mcq/teacher/:id"
            element={<McqTeacher />}
          />

          <Route
            path="/mcq/module/:id"
            element={<McqModule />}
          />

          {/* =================================================
              GENERIC FEATURE FLOW
          ================================================= */}

          <Route
            path="/:feature/grade/:id"
            element={<McqHome />}
          />

          <Route
            path="/:feature/subject/:id"
            element={<McqSubject />}
          />

          <Route
            path="/:feature/teacher/:id"
            element={<McqTeacher />}
          />

          <Route
            path="/:feature/module/:id"
            element={<McqModule />}
          />

          {/* =================================================
              FREE MCQ
          ================================================= */}

          <Route
            path="/mcq/free/teacher/:id"
            element={<FreeMCQPage />}
          />

          {/* =================================================
              FREE ESSAY
          ================================================= */}

          <Route
            path="/essay/free/teacher/:id"
            element={<EssayPage />}
          />

          {/* =================================================
              AUTH
          ================================================= */}

          <Route
            path="/auth"
            element={<AuthPage />}
          />

          <Route
            path="/dashboard"
            element={<Dashboard />}
          />

          <Route
            path="/payment"
            element={<Payment />}
          />

          {/* =================================================
              PAID MCQ
          ================================================= */}

          <Route
            path="/mcq/paid/teacher/:teacherId/:moduleId/:count"
            element={<PaidMCQPage />}
          />

          {/* =================================================
              PROFILE
          ================================================= */}

          <Route
            path="/profile"
            element={<Profile />}
          />

          <Route
            path="/profile/view"
            element={<ProfileView />}
          />

          <Route
            path="/profile/history"
            element={<ProfileHistory />}
          />

          {/* =================================================
              GRADE
          ================================================= */}

          <Route
            path="/select-grade"
            element={<GradePage />}
          />

          {/* =================================================
              STREAM
          ================================================= */}

          <Route
            path="/stream/:id"
            element={<StreamPage />}
          />

          {/* =================================================
              ESSAY
          ================================================= */}

          <Route
            path="/essay/paper/:id"
            element={<EssayPage />}
          />

          <Route
            path="/essay"
            element={<Essay />}
          />

          <Route
            path="/essay-history"
            element={<EssayHistory />}
          />

          <Route
            path="/essay/paid/teacher/:teacherId/:moduleId/:count"
            element={<EssayPaid />}
          />

          {/* =================================================
              ANIMATION
          ================================================= */}

          <Route
            path="/animation"
            element={<AnimationPage />}
          />

          {/* =================================================
              VIDEO GALLERY
          ================================================= */}

          <Route
            path="/video-gallery"
            element={<VideoGallery />}
          />

          {/* =================================================
              TEACHER DEMO
          ================================================= */}

          <Route
            path="/teacher-demo"
            element={<TeacherDemo />}
          />

          {/* =================================================
              EDUNET START
          ================================================= */}

          <Route
            path="/edunet-start"
            element={<EdunetLanding />}
          />

          {/* =================================================
              EDUCATION TYPE
          ================================================= */}

          <Route
            path="/education-type"
            element={<EducationTypePage />}
          />

          {/* =================================================
              GRADE SELECTION
          ================================================= */}

          <Route
            path="/grade-selection"
            element={<GradeSelection />}
          />

          {/* =================================================
              SUBJECT SELECTION
          ================================================= */}

          <Route
            path="/subject-selection/:gradeId"
            element={<SubjectSelection />}
          />

          {/* =================================================
              MODULE SELECTION
          ================================================= */}

          <Route
            path="/module-selection/:gradeId/:subjectId"
            element={<ModuleSelection />}
          />

          {/* =================================================
              TEACHER SELECTION
          ================================================= */}

          <Route
            path="/teacher-selection/:gradeId/:subjectId/:moduleId"
            element={<TeacherSelection />}
          />

          {/* =================================================
              TEACHER GRADE SELECTION
          ================================================= */}

          <Route
            path="/teacher-grades/:teacherId"
            element={<TeacherGradeSelection />}
          />

        </Routes>

        {/* =================================================
            FOOTER
        ================================================= */}

        {!isTeacherDemo &&
         !isEdunetPage &&
         !isAuthPage &&
         !isGradeSelection &&
         !isSubjectSelection &&
         !isModuleSelection &&
         !isTeacherSelection &&
         !isTeacherGradeSelection &&
         !isNewMCQPage && (
          <Footer />
        )}

      </div>
    </>
  );
}

// =====================================================
// APP
// =====================================================

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

// =====================================================
// EXPORT
// =====================================================

export default App;